import { useRouter } from "next/router";
import React, { useCallback, useEffect, useRef, useState } from "react";
import moment from "moment-timezone";
import { useCartSummaryAutoOpen } from "@app-contexts/public/features/basket/hooks/useCartSummaryAutoOpen";
import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import { BrandedEventDetailPage } from "@app-contexts/public/features/event-detail/components/BrandedEventDetailPage/BrandedEventDetailPage.web";
import usePublicEventService from "@app-contexts/public/features/event-detail/data/services/usePublicEventsService";
import useDetailPageScrollMargin from "@app-contexts/public/features/event-detail/hooks/useDetailPageScrollMargin";
import { buildEventSEOData } from "@app-contexts/public/features/detail-pages/utils/seo-utils";
import { useHashNavigation } from "@app-contexts/public/features/detail-pages/hooks/useHashNavigation";
import { useUserVouchersAndBasketCode } from "@app-contexts/public/features/discounts/hooks/useUserVoucherAndBasketCode/useUserVoucherAndBasketCode";
import usePublicProductCategoryService from "@app-contexts/public/features/products/data/services/usePublicProductCategoryService";
import TicketsNotAvailableText from "@app-contexts/public/features/tickets/components/TicketsNotAvailableText/TicketsNotAvailableText.web";
import { enums, PAYMENT_TYPE_OPTIONS_MAP } from "@shared/constants";
import { SubOptionsProvider } from "@shared/contexts/SubOptionsContext/SubOptionsContext";
import { useDisclosure } from "@shared/hooks/useDisclosure";
import useNativeAppCheck from "@shared/hooks/platform/useNativeAppCheck";
import TicketTypesList from "@shared/modules/events/components/tickets/TicketTypesList/TicketTypesList.web";
import ContactDetailsModalSelection from "@shared/modules/seating/components/contactDetailsModal/ContactDetailsModalSelection";
import Footer from "@ui-application/navigation/footer/Footer/Footer.web";
import { useCharity } from "@shared/modules/events/components/donations/hooks/useCharity";
import { useUserSessionAuthState } from "@shared/modules/auth/contexts/useUserSessionAuthState";
import { stringToSlug } from "@shared/utils/text/utils";
import Grid from "@ui-kit/layout/Grid/Grid.web";
import GridItem from "@ui-kit/layout/GridItem/GridItem.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import {
  eventHasWaitlistItems,
  getAllowedRegions,
  isPastEvent,
  isSoldOutEvent,
} from "@shared/modules/events/utils/event-utils";
import { shouldDisplayRating } from "@shared/modules/ratings/utils/ratings";
import DetailCardContainer from "@ui-kit/data-display/DetailCardContainer/DetailCardContainer.web";
import { useTranslation } from "@shared/utils/internationalization";
import {
  markPageStart,
  markFirstContentPainted,
  markAllComponentsReady,
} from "@app/core/src/shared/utils/datadog/datadog-timings";
import EventAuthorization from "@shared/modules/events/components/EventAuthorization/EventAuthorization.web";
import { AnalyticsService } from "@app/core/src/shared/services/analytics";
import useDjangoWaffle from "@app/core/src/shared/hooks/djangoWaffle/useDjangoWaffle";
import { PublicBaseEventSerializer } from "@shared/modules/events/types/public";
import type { BrandingConfigSerializer } from "@shared/modules/branding/data/types";
import { PublicPage } from "@/templates/PublicPage/PublicPage.web";
import { breakpoints } from "@/styles/constants";
import useMediaQuery from "@/hooks/useMediaQuery";
import { isStringWithContent } from "@/helpers/text-utils";
import lang from "@/helpers/lang";
import withEventDetail, {
  GetServerSidePropsContextWithEventData,
} from "@/gssp-subroutines/withEventDetail/withEventDetail";
import DetailPageModal from "@/components/organisms/purchases/modals/DetailPageModal";
import ProductCategoriesList from "@/components/organisms/purchases/events/ProductCategoriesList";
import RegionNotAllowedText from "@/components/organisms/purchases/events/error-text/RegionNotAllowedText";
import DetailPageLayout from "@/components/organisms/purchases/DetailPageLayout";
import DetailPageInfo from "@/components/organisms/purchases/DetailPageInfo";
import AttractionItemsTab from "@/components/organisms/attractions/AttractionItemsTab";
import EventDetailsHeader from "@/components/molecules/headers/details-page/EventDetailsHeader";
import CartSummaryFooter from "@/components/molecules/footers/CartSummaryFooter";
import EventSchema from "@/components/atoms/EventSchema";
import EventQueueIt from "@/components/atoms/EventQueueIt";

const { sm, lg } = breakpoints;

// Props used by the EventDetailPage component
interface EventDetailPageProps {
  eventData: PublicBaseEventSerializer | null;
  useNewTemplates?: boolean;
  initialBrandingConfig?: BrandingConfigSerializer | null;
}

// Full page props including layout-only props consumed by getLayout
interface EventDetailPagePropsWithLayout extends EventDetailPageProps {
  initialLayoutPreset?: "classic" | "hero" | null;
}

/**
 * Legacy Event Detail Page Component
 * Rendered when useNewTemplates is false
 */
const LegacyEventDetailPage = ({
  eventData: initialEventData,
}: {
  eventData: PublicBaseEventSerializer | null;
}): JSX.Element => {
  const { isFlagActive } = useDjangoWaffle();
  const { t } = useTranslation("purchases");
  const router = useRouter();
  const { isLoggedIn, userProfileData: user } = useUserSessionAuthState();
  const [isSmallerThanSm] = useMediaQuery(`(max-width: ${sm - 1}px)`);
  const [isTablet] = useMediaQuery(
    `(min-width: 0px) and (max-width: ${lg - 1}px)`
  );
  const {
    isOpen: isContactDetailsOpen,
    onOpen: onOpenContactDetails,
    onClose: onCloseContactDetails,
  } = useDisclosure();
  const {
    isOpen: isProductModalOpen,
    onOpen: onProductModalOpen,
    onClose: onProductModalClose,
  } = useDisclosure();
  const {
    isOpen: isTicketModalOpen,
    onOpen: onTicketModalOpen,
    onClose: onTicketModalClose,
  } = useDisclosure();

  const publicBasket = usePublicBasket();
  const { isBasketUpdating, totalItemQuantity } = publicBasket;

  const isUpdating = isBasketUpdating;

  const { isReactNativeApp } = useNativeAppCheck();

  // show map from clicking event detail header button
  const [viewMap, setViewMap] = useState(false);

  const handleViewMap = (input: boolean) => {
    setViewMap(input);
  };

  useEffect(() => {
    AnalyticsService.eventLandingPageViewed(initialEventData ?? undefined);
  }, [initialEventData]);

  // Track event page load start
  useEffect(() => {
    if (initialEventData) {
      markPageStart("event_detail_page", {
        event_id: initialEventData.id,
        event_slug: initialEventData.slug,
        is_attraction: !!initialEventData.attraction_event_config,
        venue_id: initialEventData.venue?.id,
      });
    }
  }, [initialEventData]);

  // event password variable
  const [ticketsAuthorizedByPassword, setTicketsAuthorizedByPassword] =
    useState(null);

  const updateAuthorizedTicketArray = (ticketIdArray: number[]) => {
    setTicketsAuthorizedByPassword(ticketIdArray);
  };

  const eventParams = {
    purchase_platform: enums.PURCHASE_SOURCE_PLATFORM_MAP.PLATFORM_WEB.id,
    payment_type: [
      PAYMENT_TYPE_OPTIONS_MAP.CREDIT.id,
      PAYMENT_TYPE_OPTIONS_MAP.AFTERPAY.id,
      PAYMENT_TYPE_OPTIONS_MAP.AFFIRM.id,
    ].toString(),
    source_channel: router?.query?.source_channel?.toString(),
  };

  const { useRetrieveWithMembershipTicketTypesOverride: useGetWithSlug } =
    usePublicEventService();
  const {
    data: eventData,
    isLoading,
    isError,
  } = useGetWithSlug(String(router?.query?.eventSlug), {
    config: {
      params: eventParams,
    },
    options: {
      initialData: initialEventData,
      enabled: router?.isReady && Boolean(router?.query?.eventSlug?.toString()),
    },
  });

  const { cartSummaryRef } = useCartSummaryAutoOpen({
    itemQty: totalItemQuantity,
    venueId: eventData?.venue?.id,
  });

  const { cause } = useCharity(eventData?.charities_of_choice?.[0], eventData);

  const voucherParams = {
    inputEvent: eventData,
    basketVoucherCode: null, // initial basket code value will be null
  };
  const canFetchUserVouchersAndBasketCode =
    isLoggedIn && !!eventData && !eventData.attraction_event_config;
  // load user voucher data, but only when the ticket list is displayed and the vouchers haven't already been fetched
  const { data: voucherData } = useUserVouchersAndBasketCode(voucherParams, {
    enabled: Boolean(canFetchUserVouchersAndBasketCode),
  });

  // create object to pass into purchase page info component
  const eventInfo = {
    eventId: eventData?.id,
    eventLocationName: eventData?.location?.name,
    eventAddress: `${eventData?.location?.street_name}, ${eventData?.location?.city}`,
    eventLatLng: eventData?.location?.position,
    eventVirtualType: eventData?.virtual_event_type,
    eventAccommodationUrl: eventData?.event_detail_accommodation_link || "",
    viewMap,
    handleViewMap,
  };

  const isCustomerReviewsEnabled = isFlagActive(
    "enable_customer_reviews",
    eventData?.venue?.id
  );

  // create object to pass into purchase page info component
  const venueInfo = {
    venueId: eventData?.venue?.id,
    venueSlug: eventData?.venue?.slug,
    venueDescription: eventData?.venue?.description,
    venueName: eventData?.venue?.name,
    venuePrivate: eventData?.venue?.is_test,
    venueRating: eventData?.venue?.rating,
    eventRating: eventData?.rating,
  };

  const { useList } = usePublicProductCategoryService();

  // Only fetch product categories when Add-ons modal is opened
  const { data: productCategories, isLoading: isLoadingCategories } = useList({
    params: {
      venue__slug: eventData?.venue?.slug,
    },
    options: {
      enabled: isProductModalOpen,
    },
  });

  // Track when event data is loaded and displayed
  useEffect(() => {
    if (eventData && !isLoading) {
      markFirstContentPainted("event_detail_page", {
        event_id: eventData.id,
        has_tickets: !!eventData.ticket_types?.length,
        has_products: !!eventData.has_products,
        is_sold_out: isSoldOutEvent(eventData),
      });
    }
  }, [eventData, isLoading]);

  // Track when all page components are ready
  useEffect(() => {
    if (eventData && !isLoading && !isLoadingCategories) {
      markAllComponentsReady("event_detail_page", {
        event_id: eventData.id,
        product_categories_count: productCategories?.length || 0,
        ticket_types_count: eventData.ticket_types?.length || 0,
      });
    }
  }, [eventData, isLoading, isLoadingCategories, productCategories]);

  // get tabs for normal and attraction event pages
  const getEventTabOptions = useCallback(() => {
    const tabArray = [];

    // handle tab display for normal event pages
    if (eventData && !eventData?.attraction_event_config) {
      // event description
      tabArray.push({
        id: 0,
        title: t("Additional info", { ns: "common" }),
        showTab:
          eventData?.description && isStringWithContent(eventData?.description),
        hash: "additional-info",
      });

      // important info
      tabArray.push({
        id: tabArray.length,
        title: t("Important Info"),
        showTab: eventData?.restrictions?.length > 0,
        hash: "important-info",
      });

      // event location
      tabArray.push({
        id: tabArray.length,
        title: t("Location"),
        showTab: true,
        hash: "location",
      });

      // add-ons tab
      tabArray.push({
        id: tabArray.length,
        title: t("Add-ons"),
        showTab: eventData?.has_products !== false,
        hash: null,
        openModal: onProductModalOpen,
      });

      // accommodations tab
      tabArray.push({
        id: tabArray.length,
        title: t("Accommodations"),
        showTab: eventInfo?.eventAccommodationUrl,
        hash: "accommodations",
      });
    }
    // handle tab display for attraction event pages
    else if (eventData && eventData?.attraction_event_config) {
      if (
        isFlagActive(
          "use_sections_for_attraction_page_nav",
          eventData?.venue?.id
        )
      ) {
        // display attraction sections in the tabs
        // Use spread to avoid mutating the original array
        const sortedConfigArray = [
          ...(eventData?.attraction_event_config ?? []),
        ].sort((a, b) => a.sort_order - b.sort_order);
        tabArray.push(
          ...sortedConfigArray.map((att, index: number) => {
            return {
              id: index,
              title: att.title,
              showTab: !!att.ids.length,
              hash: stringToSlug(att.title),
            };
          })
        );
      } else {
        // tickets/products/memberships tab in one
        tabArray.push({
          id: 0,
          title: eventData.no_ticket_types ? t("Free Event") : t("Tickets"),
          showTab: true,
          hash: "event-detail-page-tickets",
        });
      }

      // TODO remove dependence on attraction_event_config once donations are added to regular events
      if (
        eventData.charities_of_choice?.[0] &&
        eventData.attraction_event_config &&
        cause
      ) {
        // donations tab
        tabArray.push({
          id: tabArray.length,
          title: t("Donations"),
          showTab: true,
          hash: "donations",
        });
      }

      // event description
      tabArray.push({
        id: tabArray.length,
        title: t("Additional info", { ns: "common" }),
        showTab: true,
        hash: "additional-info",
      });

      // accommodations tab
      tabArray.push({
        id: tabArray.length,
        title: t("Accommodations"),
        showTab: eventInfo?.eventAccommodationUrl,
        hash: "accommodations",
      });
    }

    const hasDisplayableRating =
      shouldDisplayRating(venueInfo.venueRating) ||
      shouldDisplayRating(venueInfo.eventRating);
    const isAttractionEvent = Boolean(eventData?.attraction_event_config);

    if (
      isCustomerReviewsEnabled &&
      hasDisplayableRating &&
      !isAttractionEvent
    ) {
      // customer-reviews tab - show only if flag is enabled, ratings meet display threshold,
      // and it's not an attraction (attractions show reviews in Additional info tab)
      tabArray.push({
        id: tabArray.length,
        title: t("Reviews"),
        showTab: true,
        hash: "customer-reviews",
      });
    }

    return tabArray;
  }, [
    eventData,
    t,
    cause,
    onProductModalOpen,
    eventInfo.eventAccommodationUrl,
    venueInfo.venueRating,
    venueInfo.eventRating,
    isFlagActive,
    isCustomerReviewsEnabled,
  ]);

  const eventTabOptions = getEventTabOptions();

  useEffect(() => {
    if (!router.isReady) return;
    if (isError) {
      router.push({ pathname: "/404/" }, null, { shallow: true });
    } else if (!router?.query?.eventSlug) {
      router.push({ pathname: "/" }, null, { shallow: true });
    }
  }, [router.isReady, isError, router?.query?.eventSlug, router]);

  // initialize the selected tab to nothing
  const [selectedTab, setSelectedTab] = useState(-1);
  // Disable scroll-based tab sync during URL navigation to prevent conflicts
  const [scrollSyncDisabled, setScrollSyncDisabled] = useState(false);

  const scrollMargin = useDetailPageScrollMargin();

  // Track if scrolling should be skipped (after manual tab click)
  const skipNextScrollRef = useRef<boolean>(false);

  // Handle URL hash changes (browser back/forward navigation)
  const { markHashProcessed } = useHashNavigation({
    isReady: router.isReady && !isLoading && !!eventData,
    asPath: router?.asPath,
    tabOptions: getEventTabOptions(),
    setSelectedTab,
    hashRedirects: { description: "additional-info" },
    onRedirect: (hash) =>
      router.replace({ hash }, undefined, { shallow: true }),
    skipScrollRef: skipNextScrollRef,
    onScrollSyncChange: setScrollSyncDisabled,
  });

  const handleManualTabChange = useCallback(
    (tabId: number, hash?: string) => {
      skipNextScrollRef.current = true;
      // Update the processed hash to prevent re-processing when URL updates
      if (hash) {
        markHashProcessed(hash);
      }
      setSelectedTab(tabId);
      // Reset the flag after a short delay
      setTimeout(() => {
        skipNextScrollRef.current = false;
      }, 500);
    },
    [setSelectedTab, markHashProcessed]
  );

  const isLoadingPage = (isLoading && !initialEventData) || isError;

  const allowedRegions = getAllowedRegions(eventData?.allowed_regions);
  const displayAllowedRegionsText =
    allowedRegions && !eventData?.region_is_allowed;

  const hasTicketTypes = eventData?.ticket_types?.length > 0;
  const hasChildEvents = eventData?.child_events?.length > 0;
  const ticketsNotRequired = eventData?.no_ticket_types;

  const soldOutEvent = eventData && isSoldOutEvent(eventData);
  const hasWaitlistItems = eventData && eventHasWaitlistItems(eventData);
  const pastEvent = eventData && isPastEvent(eventData);
  const recurringEvent = eventData?.is_recurring_parent;

  const displayTicketsNotAvailableText =
    ticketsNotRequired || // event is free
    pastEvent || // event has ended
    (soldOutEvent && !hasWaitlistItems) || // event is sold out AND has no waitlist items
    (!recurringEvent && !hasTicketTypes) || // normal event with no ticket types
    (recurringEvent && !hasChildEvents); // recurring event with no child events

  const getModalSubtitle = () => {
    let text = `${moment(eventData?.starts_on)
      ?.tz(eventData?.timezone)
      ?.format("dddd MMM D, YYYY | h:mm A z")}`;

    if (recurringEvent && hasChildEvents) {
      text = "";
    } else if (eventData?.date_time_to_be_determined) {
      text = t("Date and Time TBD");
    }
    return text;
  };

  const getAttractionLandingDisplay = () => {
    const descriptionTabId = eventTabOptions.find(
      (tab) => tab.hash === "additional-info"
    )?.id;

    // return all of the attraction components on a single tab
    let scrollId = "event-detail-page-tickets";
    let returnElement = (
      <AttractionItemsTab
        basket={publicBasket}
        event={eventData}
        isBasketUpdating={isUpdating}
        attractionItems={eventData?.attraction_event_config}
        scrollMargin={scrollMargin}
      />
    );

    // return the event description page
    if (selectedTab === descriptionTabId) {
      scrollId = "additional-info";
      returnElement = (
        <DetailPageInfo
          infoHeader={t("Description")}
          infoRestrictions={eventData?.restrictions}
          infoText={eventData?.description}
          refundPolicyText={
            eventData?.refund_policy || eventData?.venue?.default_refund_policy
          }
          openContactDetailsModal={onOpenContactDetails}
          isLoading={isLoading}
          eventInfo={eventInfo}
          venueInfo={venueInfo}
          isAttractionPage={!!eventData?.attraction_event_config}
        />
      );
    }

    return (
      <Flex id={scrollId} scrollMarginTop={scrollMargin}>
        {returnElement}
      </Flex>
    );
  };

  const handleReviewsOnClick = () => {
    const reviewsTab = eventTabOptions.find(
      (tab) => tab.hash === "customer-reviews"
    );
    if (reviewsTab) {
      handleManualTabChange(reviewsTab.id);
      router.replace(
        {
          hash: reviewsTab.hash,
        },
        undefined,
        { shallow: true }
      );
    }
  };

  const getNormalEventDisplay = () => {
    // display the event info page
    return (
      <Flex
        id="additional-info"
        direction="column"
        scrollMarginTop={scrollMargin}
        gap="md"
      >
        {/* Display region not allowed text */}
        {displayAllowedRegionsText && !isLoading && (
          <DetailCardContainer>
            <RegionNotAllowedText allowedRegions={allowedRegions} />
          </DetailCardContainer>
        )}

        {/* Display tickets not available text */}
        {displayTicketsNotAvailableText && !isLoading && (
          <DetailCardContainer>
            <TicketsNotAvailableText
              pastEvent={pastEvent}
              soldOutEvent={soldOutEvent}
              ticketsNotRequired={ticketsNotRequired}
              waitlistedEvent={hasWaitlistItems}
              customTicketMessage={eventData?.no_ticket_types_message}
            />
          </DetailCardContainer>
        )}
        <DetailPageInfo
          infoHeader={t("Description")}
          infoText={eventData?.description}
          infoRestrictions={eventData?.restrictions}
          refundPolicyText={
            eventData?.refund_policy || eventData?.venue?.default_refund_policy
          }
          openContactDetailsModal={onOpenContactDetails}
          isLoading={isLoading}
          eventInfo={eventInfo}
          venueInfo={venueInfo}
          isAttractionPage={!!eventData?.attraction_event_config}
        />
      </Flex>
    );
  };

  return (
    <Flex h="full" w="full" direction="column" flex={1} bg="bg.subtle">
      {/* Contact details modal, opens from the Contact Information section and event header */}
      <ContactDetailsModalSelection
        venueId={venueInfo.venueId}
        isOpen={isContactDetailsOpen}
        onClose={onCloseContactDetails}
        user={user}
        isLoggedIn={isLoggedIn}
        refundPolicyText={
          eventData?.refund_policy || eventData?.venue?.default_refund_policy
        }
      />
      {/* List of ticket types for purchase */}
      <SubOptionsProvider>
        <DetailPageModal
          title={t("Tickets")}
          subtitle={getModalSubtitle()}
          isOpen={isTicketModalOpen}
          onClose={onTicketModalClose}
          venue={eventData?.venue}
          itemListElement={
            <TicketTypesList
              eventData={eventData}
              isLoading={isLoading}
              ticketsAuthorizedByPassword={ticketsAuthorizedByPassword}
              basketVoucherCode={voucherData?.basketVoucherCode}
              userVouchers={voucherData?.userVouchers}
            />
          }
        />
      </SubOptionsProvider>
      {/* List of products for purchase */}
      <DetailPageModal
        title={t("Add-ons")}
        isOpen={isProductModalOpen}
        onClose={onProductModalClose}
        venue={eventData?.venue}
        itemListElement={
          <ProductCategoriesList
            eventData={eventData}
            productCategories={productCategories ?? []}
            isLoading={isLoadingCategories}
          />
        }
      />

      {/* Rich schema implementation for event object */}
      {eventData && <EventSchema event={eventData} />}

      {/* Queue-it script integration  */}
      {eventData && <EventQueueIt event={eventData} />}

      {/* Hide page content (including header content) if there's password protection */}

      <EventAuthorization
        isLoading={isLoadingPage}
        eventData={eventData}
        ticketsAuthorizedByPassword={ticketsAuthorizedByPassword}
        updateAuthorizedTicketArray={updateAuthorizedTicketArray}
        gateChildrenUntilReady
      >
        {/* Navbar sections that display event information/tabs */}

        <EventDetailsHeader
          eventTabOptions={eventTabOptions}
          selectedTab={selectedTab}
          setSelectedTab={handleManualTabChange}
          openContactDetailsModal={onOpenContactDetails}
          isSmallerThanSm={isSmallerThanSm}
          isTablet={isTablet}
          isReactNativeApp={isReactNativeApp}
          handleViewMap={handleViewMap}
          openModal={onTicketModalOpen}
          isAttractionPage={!!eventData?.attraction_event_config}
          reviewsOnClick={
            isCustomerReviewsEnabled ? handleReviewsOnClick : undefined
          }
          scrollSyncDisabled={scrollSyncDisabled}
        />
        <Box
          w="full"
          mx="auto"
          flexGrow={1}
          maxWidth="container.content"
          px={{ base: "none", sm: "3xl" }}
          bg="bg.subtle"
        >
          <Flex
            py={{
              base: "4",
              lg: eventData?.attraction_event_config ? "4" : "6",
            }}
            direction="column"
          >
            <Grid
              h="full"
              width="full"
              gap="xl"
              templateColumns="repeat(5, 1fr)"
            >
              <GridItem colSpan={{ base: 5, lg: 3 }} height="fit-content">
                {eventData?.attraction_event_config
                  ? getAttractionLandingDisplay()
                  : getNormalEventDisplay()}
              </GridItem>
            </Grid>
          </Flex>
        </Box>
      </EventAuthorization>

      <Box display={{ base: "none", sm: "block" }}>
        <Footer isReactNativeApp={isReactNativeApp} />
      </Box>
      {eventData?.attraction_event_config && (
        <CartSummaryFooter
          ref={cartSummaryRef}
          showOnTablet
          venue={eventData?.venue}
        />
      )}
    </Flex>
  );
};

/**
 * Main Event Detail Page Component
 * Conditionally renders branded or legacy page based on server-side flag check
 */
const EventDetailPage = ({
  eventData: initialEventData,
  useNewTemplates,
  initialBrandingConfig,
}: EventDetailPageProps): JSX.Element => {
  // Render branded page if flag is enabled (determined server-side)
  if (useNewTemplates && initialEventData) {
    return (
      <BrandedEventDetailPage
        eventData={initialEventData}
        initialBrandingConfig={initialBrandingConfig}
      />
    );
  }

  // Legacy page rendering below
  return <LegacyEventDetailPage eventData={initialEventData} />;
};

export default EventDetailPage;

// Type for getLayout to access all page props (including layout-only ones)
type EventDetailPageWithLayout = typeof EventDetailPage & {
  getLayout: (
    page: React.ReactElement<EventDetailPagePropsWithLayout>
  ) => JSX.Element;
};

(EventDetailPage as EventDetailPageWithLayout).getLayout = function getLayout(
  page: React.ReactElement<EventDetailPagePropsWithLayout>
) {
  // Use PublicPage layout for branded pages, DetailPageLayout for legacy
  if (page.props.useNewTemplates) {
    return (
      <PublicPage
        initialLayoutPreset={page.props.initialLayoutPreset}
        seo={buildEventSEOData(page.props.eventData ?? undefined)}
      >
        {page}
      </PublicPage>
    );
  }

  return (
    <DetailPageLayout
      itemData={page.props.eventData}
      analyticsLabel={enums.INTERACTION_LABEL_MAP.EVENT_PAGE.id}
      trackingTokenLocation={
        enums.TRACKING_TOKEN_LOCATIONS.LOCATION_PUBLIC_EVENT.id
      }
    >
      {page}
    </DetailPageLayout>
  );
};

export const getServerSideProps = withEventDetail(
  async ({
    locale,
    eventData,
    useNewTemplates,
    brandingConfig,
    initialThemeConfig,
    initialLayoutPreset,
  }: GetServerSidePropsContextWithEventData) => {
    if (
      eventData?.external_link &&
      eventData?.third_party_redirect_option ===
        enums.THIRD_PARTY_REDIRECT_OPTIONS_BY_ID.page_load.id
    ) {
      return {
        redirect: {
          permanent: false,
          destination: eventData.external_link,
        },
      };
    }

    return {
      props: {
        ...(await lang(locale, [
          "purchases",
          "account",
          "constants",
          "widget",
          "seating",
          "exchanges",
        ])),
        eventData: eventData ?? null,
        useNewTemplates: useNewTemplates ?? false,
        ...(useNewTemplates
          ? {
              initialBrandingConfig: brandingConfig
                ? JSON.parse(JSON.stringify(brandingConfig))
                : null,
              initialThemeConfig: initialThemeConfig
                ? JSON.parse(JSON.stringify(initialThemeConfig))
                : null,
              initialLayoutPreset: initialLayoutPreset ?? null,
            }
          : {}),
      },
    };
  }
);
